import React, { useState } from "react";
import { Button, IconButton, Tooltip, useToast } from "@chakra-ui/react";
import { UnlockIcon } from "@chakra-ui/icons";
import adminApi from "../../connection/adminApi";

/**
 * Opens the store's own Sylius admin, signed in as one of its administrators.
 *
 * The link is minted per click and dies in two minutes, so it is fetched on
 * demand rather than rendered into the page. The tab is opened before the
 * request is awaited: a popup blocker only trusts window.open while the click
 * is still being handled.
 */
const LoginAsButton = ({
  storeId,
  adminUserId,
  label = "Log in as admin",
  size = "sm",
  variant = "solid",
  colorScheme = "purple",
  iconOnly = false,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const open = async () => {
    setLoading(true);
    // Opened blank and up front: a popup blocker only trusts window.open while
    // the click is still being handled, and the URL is not known until the
    // request comes back. "noopener" is deliberately not passed — it makes
    // window.open return null, leaving no handle to redirect.
    const tab = window.open("about:blank", "_blank");

    try {
      const { data } = await adminApi.get(
        `api/admin/stores/${storeId}/login-link`,
        { params: adminUserId ? { adminUserId } : {} }
      );

      if (tab) {
        // The store admin must not keep a handle back to the console.
        tab.opener = null;
        tab.location.replace(data.url);
      } else {
        // Popups blocked: a link click still opens a tab, and the console page
        // is never navigated away from.
        const link = document.createElement("a");
        link.href = data.url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      toast({
        status: "info",
        duration: 5000,
        title: `Signing in as ${data.adminUser.username}`,
        description: data.host,
      });
    } catch (error) {
      if (tab) {
        tab.close();
      }

      toast({
        status: "error",
        duration: 10000,
        isClosable: true,
        title: "Could not open the store admin",
        description:
          error?.response?.data?.message ||
          "No login link could be created for this store.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (iconOnly) {
    return (
      <Tooltip label={label}>
        <IconButton
          aria-label={label}
          icon={<UnlockIcon />}
          size={size}
          variant={variant}
          colorScheme={colorScheme}
          isLoading={loading}
          onClick={open}
          // Icons size to the font, so this is what makes the glyph bigger.
          fontSize="lg"
        />
      </Tooltip>
    );
  }

  return (
    <Button
      size={size}
      variant={variant}
      colorScheme={colorScheme}
      leftIcon={<UnlockIcon />}
      isLoading={loading}
      onClick={open}
    >
      {label}
    </Button>
  );
};

export default LoginAsButton;
